import {
  Box,
  Button,
  InputLabel,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTheme } from '@mui/material/styles';
import React, { FC, useEffect, useState } from 'react';
import useResponsive from '../../hooks/useResponsive';

import { InputCustomSize, TextArea } from '../UIElements/input-ui';
import SelectInput from '../UIElements/select';
import { JobIdDetailsProps } from '../../interface/job_board';
import { labelStyles } from '../UIElements/styles-input';
import {
  extractContractDates,
  formatDate,
  formatTextType,
  separateDocsByComma,
} from '../../utils/url-id';
import { textSty } from '../UIElements/styles-typography';
import { useMutation } from '@tanstack/react-query';
import { apiUpdateJob } from '../../api/jobs';
import { useNotificationService } from '../notification';

const JobDetails: FC<{
  jobDetails: JobIdDetailsProps;
  jobId: string;
  onUpdated: () => void;
}> = ({ jobDetails, jobId, onUpdated }) => {
  const theme = useTheme();
  const isMedium = useResponsive('down', 'md', 'xl');
  const { showNotification } = useNotificationService();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    job_title: '',
    department: '',
    job_description: '',
    qualifications: '',
    experience_level: '',
    employment_type: '',
    work_schedule: '',
    work_type: '',
    contract_duration: '',
    work_location: '',
    salary_range: '',
    application_deadline: '',
    additional_info: '',
    required_documents: '',
    how_to_apply: '',
    external_link: '',
  });

  const headerSty = {
    fontFamily: 'Lexend',
    fontWeight: 500,
    my: isMedium ? 2 : 3,
    color: theme.palette.neutral.light,
  };

  useEffect(() => {
    setForm({
      job_title: jobDetails?.job_title ?? '',
      department: jobDetails?.department ?? '',
      job_description: jobDetails?.job_description ?? '',
      qualifications: (jobDetails as any)?.qualifications ?? '',
      experience_level: jobDetails?.experience_level ?? '',
      employment_type: jobDetails?.employment_type ?? '',
      work_schedule: jobDetails?.work_schedule ?? '',
      work_type: jobDetails?.work_type ?? '',
      contract_duration: jobDetails?.contract_duration ?? '',
      work_location: jobDetails?.work_location ?? '',
      salary_range: jobDetails?.salary_range ?? '',
      application_deadline: jobDetails?.application_deadline
        ? new Date(jobDetails.application_deadline).toISOString().slice(0, 10)
        : '',
      additional_info: jobDetails?.additional_info ?? '',
      required_documents: jobDetails?.required_documents ?? '',
      how_to_apply: jobDetails?.how_to_apply ?? '',
      external_link: jobDetails?.external_link ?? '',
    });
  }, [jobDetails]);

  const mutation = useMutation({
    mutationFn: () =>
      apiUpdateJob({
        jobId,
        fieldValues: {
          ...form,
          application_deadline: form.application_deadline
            ? new Date(form.application_deadline).toISOString()
            : undefined,
        },
      }),
    onSuccess: (data) => {
      const response = data as any;
      if (response?.status === 'error') {
        showNotification(response?.msg ?? 'Job update failed', 'error');
        return;
      }
      showNotification(response?.data?.message ?? 'Job updated', 'success');
      setEditing(false);
      onUpdated();
    },
    onError: () => {
      showNotification('Job update failed', 'error');
    },
  });

  const updateForm = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  if (editing) {
    return (
      <Box>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 3 }}>
          <Typography sx={{ ...headerSty, my: 0 }} variant="subtitle2">
            Edit Job
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              onClick={() => setEditing(false)}
              sx={{ borderRadius: '12px' }}
            >
              Cancel
            </Button>
            <LoadingButton
              loading={mutation.isPending}
              onClick={() => mutation.mutate()}
              variant="contained"
              sx={{ borderRadius: '12px' }}
            >
              Save
            </LoadingButton>
          </Stack>
        </Stack>

        <Typography sx={{ ...headerSty, my: 0 }} variant="subtitle2">
          Job Details
        </Typography>
        <Stack
          direction={isMedium ? 'column' : 'row'}
          alignItems="center"
          justifyContent="space-between"
          sx={{ my: 2, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <InputLabel sx={{ ...labelStyles, mb: 1 }}>Job Title</InputLabel>
            <InputCustomSize
              placeholder="Job title"
              value={form.job_title}
              onchange={(e: any) => updateForm('job_title', e.target.value)}
              type="text"
              size="medium"
              styles={undefined}
            />
          </Stack>
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <InputLabel sx={{ ...labelStyles, mb: 1 }}>Department</InputLabel>
            <InputCustomSize
              placeholder="Department"
              value={form.department}
              onchange={(e: any) => updateForm('department', e.target.value)}
              type="text"
              size="medium"
              styles={undefined}
            />
          </Stack>
        </Stack>
        <Stack sx={{ width: '100%', my: 2 }}>
          <InputLabel sx={{ ...labelStyles, mb: 1 }}>Job Description</InputLabel>
          <TextArea
            placeholder="Job description"
            value={form.job_description}
            onchange={(e: any) =>
              updateForm('job_description', e.target.value)
            }
          />
        </Stack>
        <Stack
          direction={isMedium ? 'column' : 'row'}
          alignItems="center"
          justifyContent="space-between"
          sx={{ my: 2, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <InputLabel sx={{ ...labelStyles, mb: 1 }}>Qualifications</InputLabel>
            <InputCustomSize
              placeholder="Qualifications"
              value={form.qualifications}
              onchange={(e: any) =>
                updateForm('qualifications', e.target.value)
              }
              type="text"
              size="medium"
              styles={undefined}
            />
          </Stack>
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <InputLabel sx={{ ...labelStyles, mb: 1 }}>
              Experience Level
            </InputLabel>
            <InputCustomSize
              placeholder="Experience level"
              value={form.experience_level}
              onchange={(e: any) =>
                updateForm('experience_level', e.target.value)
              }
              type="text"
              size="medium"
              styles={undefined}
            />
          </Stack>
        </Stack>

        <Typography sx={{ ...headerSty, my: 2 }} variant="subtitle2">
          Employment Type & Schedule
        </Typography>
        <Stack
          direction={isMedium ? 'column' : 'row'}
          alignItems="center"
          justifyContent="space-between"
          sx={{ my: 2, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <SelectInput
              label="Employment Type"
              value={form.employment_type}
              onChange={(e) => updateForm('employment_type', e.target.value)}
              options={[
                { value: 'full time', label: 'Full Time' },
                { value: 'short locum', label: 'Short Locum' },
                { value: 'long locum', label: 'Long Locum' },
              ]}
            />
          </Stack>
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <InputLabel sx={{ ...labelStyles, mb: 1 }}>Work Schedule</InputLabel>
            <InputCustomSize
              placeholder="Work schedule"
              value={form.work_schedule}
              onchange={(e: any) =>
                updateForm('work_schedule', e.target.value)
              }
              type="text"
              size="medium"
              styles={undefined}
            />
          </Stack>
        </Stack>
        <Stack
          direction={isMedium ? 'column' : 'row'}
          alignItems="center"
          justifyContent="space-between"
          sx={{ my: 2, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <SelectInput
              label="Work Type"
              value={form.work_type}
              onChange={(e) => updateForm('work_type', e.target.value)}
              options={[
                { value: 'on-site', label: 'On site' },
                { value: 'remote', label: 'Remote' },
                { value: 'hybrid', label: 'Hybrid' },
              ]}
            />
          </Stack>
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <InputLabel sx={{ ...labelStyles, mb: 1 }}>
              Contract Duration
            </InputLabel>
            <InputCustomSize
              placeholder="Contract duration"
              value={form.contract_duration}
              onchange={(e: any) =>
                updateForm('contract_duration', e.target.value)
              }
              type="text"
              size="medium"
              styles={undefined}
            />
          </Stack>
        </Stack>

        <Typography sx={{ ...headerSty, my: 2 }} variant="subtitle2">
          Location & Compensation
        </Typography>
        <Stack
          direction={isMedium ? 'column' : 'row'}
          alignItems="center"
          justifyContent="space-between"
          sx={{ my: 2, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <InputLabel sx={{ ...labelStyles, mb: 1 }}>Work Location</InputLabel>
            <InputCustomSize
              placeholder="Work location"
              value={form.work_location}
              onchange={(e: any) =>
                updateForm('work_location', e.target.value)
              }
              type="text"
              size="medium"
              styles={undefined}
            />
          </Stack>
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <InputLabel sx={{ ...labelStyles, mb: 1 }}>Salary Range</InputLabel>
            <InputCustomSize
              placeholder="Salary range"
              value={form.salary_range}
              onchange={(e: any) => updateForm('salary_range', e.target.value)}
              type="text"
              size="medium"
              styles={undefined}
            />
          </Stack>
        </Stack>

        <Typography sx={{ ...headerSty, my: 2 }} variant="subtitle2">
          Application Details
        </Typography>
        <Stack
          direction={isMedium ? 'column' : 'row'}
          alignItems="center"
          justifyContent="space-between"
          sx={{ my: 2, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <InputLabel sx={{ ...labelStyles, mb: 1 }}>
              Application Deadline
            </InputLabel>
            <InputCustomSize
              placeholder="Application deadline"
              value={form.application_deadline}
              onchange={(e: any) =>
                updateForm('application_deadline', e.target.value)
              }
              type="date"
              size="medium"
              styles={undefined}
            />
          </Stack>
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <InputLabel sx={{ ...labelStyles, mb: 1 }}>
              Required Documents
            </InputLabel>
            <InputCustomSize
              placeholder="Required documents"
              value={form.required_documents}
              onchange={(e: any) =>
                updateForm('required_documents', e.target.value)
              }
              type="text"
              size="medium"
              styles={undefined}
            />
          </Stack>
        </Stack>
        <Stack sx={{ width: '100%', my: 2 }}>
          <InputLabel sx={{ ...labelStyles, mb: 1 }}>How to Apply</InputLabel>
          <TextArea
            placeholder="How to apply"
            value={form.how_to_apply}
            onchange={(e: any) => updateForm('how_to_apply', e.target.value)}
          />
        </Stack>
        <Stack sx={{ width: '100%', my: 2 }}>
          <InputLabel sx={{ ...labelStyles, mb: 1 }}>
            External Application Link
          </InputLabel>
          <InputCustomSize
            placeholder="https://example.com/apply"
            value={form.external_link}
            onchange={(e: any) => updateForm('external_link', e.target.value)}
            type="url"
            size="medium"
            styles={undefined}
          />
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{}}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ ...headerSty, my: 0 }} variant="subtitle2">
          Job Details
        </Typography>
        <Button
          onClick={() => setEditing(true)}
          variant="contained"
          sx={{ borderRadius: '12px' }}
        >
          Edit Job
        </Button>
      </Stack>
      <Typography sx={{ ...headerSty, my: 2 }} variant="subtitle2">
        Job Type
      </Typography>
      <Box>
        <Stack direction={'row'} sx={{ gap: 2, my: 2 }}>
          <Typography sx={{ ...textSty, mb: 3 }}>Job Type</Typography>
          <Typography sx={{ ...labelStyles }}>
            {' '}
            {formatTextType(jobDetails?.job_type ?? '')}
          </Typography>
        </Stack>
        <Typography sx={{ ...headerSty, my: 0 }} variant="subtitle2">
          Job Details
        </Typography>
        <Stack
          direction={isMedium ? 'column' : 'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <Typography sx={{ ...textSty, mb: 0 }}>Job Title</Typography>
            <Typography sx={{ ...labelStyles }}>
              {jobDetails?.job_title}
            </Typography>
          </Stack>
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <Typography sx={{ ...textSty, mb: 0 }}>Department</Typography>
            <Typography sx={{ ...labelStyles }}>
              {formatTextType(jobDetails?.department ?? '')}
            </Typography>
          </Stack>
        </Stack>
        <Stack sx={{ width: '100%', my: 2 }}>
          <Typography sx={{ ...textSty, mb: 0 }}>Job Description:</Typography>
          <Typography sx={{ ...labelStyles }}>
            {jobDetails?.job_description}
          </Typography>
        </Stack>
        <Stack
          direction={isMedium ? 'column' : 'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{ my: 3, gap: 2 }}
        >
          {/* <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
              <Typography sx={{ ...textSty, mb: 0 }}>Qualifications</Typography>
              <Typography sx={{ ...labelStyles }}>
                {jobDetails?.quali}
              </Typography>
            </Stack> */}
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <Typography sx={{ ...textSty, mb: 0 }}>
              Experience Level:
            </Typography>
            <Typography sx={{ ...labelStyles }}>
              {formatTextType(jobDetails?.experience_level)}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      {/*  */}
      <Box>
        <Typography sx={{ ...headerSty, my: 0, mb: 2 }} variant="subtitle2">
          Employment Type & Schedule
        </Typography>
        <Stack
          direction={isMedium ? 'column' : 'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <Typography sx={{ ...textSty, mb: 0 }}>Employment Type:</Typography>
            <Typography sx={{ ...labelStyles }}>
              {formatTextType(jobDetails?.employment_type)}
            </Typography>
          </Stack>
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <Typography sx={{ ...textSty, mb: 0 }}>Work Schedule:</Typography>
            <Typography sx={{ ...labelStyles }}>
              {formatTextType(jobDetails?.work_schedule)}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          direction={isMedium ? 'column' : 'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <Typography sx={{ ...textSty, mb: 0 }}>
              Contract Duration (For Temporary Jobs):
            </Typography>
            <Typography sx={{ ...labelStyles }}>
              {extractContractDates(jobDetails?.contract_duration ?? '')}
            </Typography>
          </Stack>
        </Stack>
      </Box>
      <Box>
        <Typography sx={{ ...headerSty, my: 0, mb: 2 }} variant="subtitle2">
          Location & Compensation
        </Typography>
        <Stack
          direction={isMedium ? 'column' : 'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <Typography sx={{ ...textSty, mb: 0 }}>Work Location:</Typography>
            <Typography sx={{ ...labelStyles }}>
              {formatTextType(jobDetails?.work_location ?? '')}
            </Typography>
          </Stack>
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <Typography sx={{ ...textSty, mb: 0 }}>Salary Range:</Typography>
            <Typography sx={{ ...labelStyles }}>
              {formatTextType(jobDetails?.salary_range)}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          direction={isMedium ? 'column' : 'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <Typography sx={{ ...textSty, mb: 0 }}>
              Additional Benefits:
            </Typography>
            <Typography sx={{ ...labelStyles }}>
              {formatTextType(jobDetails?.additional_info ?? '')}
            </Typography>
          </Stack>
        </Stack>
      </Box>
      <Box>
        <Typography sx={{ ...headerSty, my: 0, mb: 2 }} variant="subtitle2">
          Application Details
        </Typography>
        <Stack
          direction={isMedium ? 'column' : 'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <Typography sx={{ ...textSty, mb: 0 }}>
              Application Deadline:
            </Typography>
            <Typography sx={{ ...labelStyles }}>
              {formatDate(jobDetails?.application_deadline)}
            </Typography>
          </Stack>
          <Stack sx={{ width: isMedium ? '100%' : '49%' }}>
            <Typography sx={{ ...textSty, mb: 0 }}>
              Required Documents:
            </Typography>
            {separateDocsByComma?.(jobDetails?.required_documents)?.map(
              (val, id) => (
                <Typography key={id} sx={{ ...labelStyles }}>
                  [✓] {val}
                </Typography>
              )
            )}
          </Stack>
        </Stack>
        <Stack
          direction={isMedium ? 'column' : 'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: '100%' }}>
            <Typography sx={{ ...textSty, mb: 0 }}>How to apply:</Typography>
            <Typography sx={{ ...labelStyles }}>
              {jobDetails?.how_to_apply ?? '-'}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          direction={isMedium ? 'column' : 'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: '100%' }}>
            <Typography sx={{ ...textSty, mb: 0 }}>
              External Application Link:
            </Typography>
            {jobDetails?.external_link ? (
              <Typography
                component="a"
                href={jobDetails.external_link}
                target="_blank"
                rel="noreferrer"
                sx={{
                  ...labelStyles,
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  wordBreak: 'break-word',
                }}
              >
                {jobDetails.external_link}
              </Typography>
            ) : (
              <Typography sx={{ ...labelStyles }}>-</Typography>
            )}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default JobDetails;
